import React, { useState, useRef, useEffect } from 'react';
import './QueryInterface.css';
import MicIcon from '@mui/icons-material/Mic';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const QueryInterface = ({ 
  onSubmit, 
  isLoading, 
  currentQuery, 
  onQueryChange 
}) => {
  const [localQuery, setLocalQuery] = useState(currentQuery || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  // Character limit
  const MAX_CHARS = 500;

  // Sync with external query changes
  useEffect(() => {
    if (currentQuery !== localQuery) {
      setLocalQuery(currentQuery || '');
    }
  }, [currentQuery]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        Math.max(textareaRef.current.scrollHeight, 72), // min 3 lines
        192 // max 8 lines
      )}px`;
    }
  }, [localQuery]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setLocalQuery(transcript);
        onQueryChange(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [onQueryChange]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS) {
      setLocalQuery(value);
      onQueryChange(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (localQuery.trim() && !isLoading) {
      onSubmit(localQuery.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const startVoiceInput = () => {
    if (recognitionRef.current && !isListening) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopVoiceInput = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const quickSuggestions = [
    "Show me last weeks sales",
    "Top selling products this week",
    "Top 5 customers last month",
    "Revenue trends by product category"
  ];

  const isVoiceSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  return (
    <div className="query-interface">
      <form onSubmit={handleSubmit} className="query-form">
        <div className={`query-input-container ${isFocused ? 'focused' : ''} ${isLoading ? 'loading' : ''}`}>
          <textarea
            ref={textareaRef}
            value={localQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Type your question here... (e.g., 'Show me top 5 customers this month')"
            className="query-textarea"
            disabled={isLoading}
            rows={3}
            maxLength={MAX_CHARS}
          />
          
          <div className="input-actions">
            {isVoiceSupported && (
              <button
                type="button"
                className={`voice-button ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopVoiceInput : startVoiceInput}
                disabled={isLoading}
                title={isListening ? 'Stop listening' : 'Start voice input'}
              >
                {isListening ? <MicIcon color="error" /> : <MicIcon />}
              </button>
            )}
            
            <button
              type="submit"
              className={`send-button ${localQuery.trim() ? 'active' : ''}`}
              disabled={!localQuery.trim() || isLoading}
              title="Send query"
            >
              {isLoading ? (
                <div className="loading-spinner" />
              ) : (
                <RocketLaunchIcon />
              )}
            </button>
          </div>
        </div>
        
        <div className="input-footer">
          <div className="character-counter">
            <span className={localQuery.length > MAX_CHARS * 0.9 ? 'warning' : ''}>
              {localQuery.length}/{MAX_CHARS}
            </span>
          </div>
          
          {isListening && (
            <div className="listening-indicator">
              <span className="listening-dot" />
              Listening...
            </div>
          )}
        </div>
      </form>

      {/* Quick suggestions */}
      {isFocused && !localQuery && (
        <div className="quick-suggestions">
          <div className="suggestions-header">
            <span className="lightbulb">💡</span>
            Quick suggestions:
          </div>
          <div className="suggestions-list">
            {quickSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className="suggestion-pill"
                onMouseDown={(e) => {
                  // Prevent blur event on textarea
                  e.preventDefault();
                }}
                onClick={() => {
                  setLocalQuery(suggestion);
                  onQueryChange?.(suggestion);
                  // Use setTimeout to ensure the suggestion is set before focusing
                  setTimeout(() => {
                    textareaRef.current?.focus();
                  }, 0);
                }}
                style={{ 
                  animationDelay: `${index * 0.1}s` 
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryInterface; 